import React, { useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { formatCurrency, MICRO_CONVERSION } from '@/utils/money';
import {
  PRICE_INPUT_REGEX,
  stripCommas,
} from '@/domain/pricing/utils/pricing-utils';
import { TextInput, View } from 'react-native';
import { useReportCellFocus } from '@experiences/components/table/blocs/hooks/use-report-cell-focus';

interface EditablePerUnitCellProps {
  currencyCode: string;
  localId: string;
  /** Edits commit as `perUnit × unitsPerPack` via UPDATE_PRICE. */
  onUpdatePrice: (localId: string, price: string) => void;
  /** Current pack price string (the editor's stored field). */
  ourPrice: string;
  rowIndex: number;
  /** Pack-to-unit ratio; absent → cell renders nothing. */
  unitsPerPack?: number;
}

const formatPerUnit = (
  ourPrice: string,
  unitsPerPack: number,
  currencyCode: string,
): string => {
  const packPrice = parseFloat(ourPrice);
  if (isNaN(packPrice)) {
    return '';
  }
  return formatCurrency(
    (packPrice / unitsPerPack) * MICRO_CONVERSION,
    currencyCode,
  );
};

// Per-unit rounded to cents as a plain numeric string ("0.42"), not the
// formatted "$0.42" — used as the focused/editable value.
const perUnitToCentsString = (
  packPrice: number,
  unitsPerPack: number,
): string => (Math.round((packPrice / unitsPerPack) * 100) / 100).toString();

/** Editable Per-Unit cell. Idle: shows `ourPrice / unitsPerPack` formatted.
 *  Commit: dispatches UPDATE_PRICE with the recomputed pack price. */
export const EditablePerUnitCell: React.FC<EditablePerUnitCellProps> = ({
  currencyCode,
  localId,
  onUpdatePrice,
  ourPrice,
  rowIndex,
  unitsPerPack,
}) => {
  const [styles, theme] = useStyles();
  const [isFocused, setIsFocused] = useState(false);
  const [value, setValue] = useState('');
  const reportCellFocus = useReportCellFocus(rowIndex);

  // Hide when there's no meaningful sub-unit (no catalog data, or pack === unit).
  if (unitsPerPack === undefined || unitsPerPack <= 1) {
    return null;
  }

  const handleFocus = () => {
    reportCellFocus();
    const packPrice = parseFloat(ourPrice);
    setValue(
      isNaN(packPrice) ? '' : perUnitToCentsString(packPrice, unitsPerPack),
    );
    setIsFocused(true);
  };

  const handleChangeText = (text: string) => {
    if (text && !PRICE_INPUT_REGEX.test(text)) {
      return;
    }
    setValue(stripCommas(text));
  };

  const handleBlur = () => {
    setIsFocused(false);
    const parsed = parseFloat(value);
    if (isNaN(parsed) || parsed <= 0) {
      // Reset local state to the last-known per-unit so a re-focus doesn't
      // start with the rejected input.
      setValue(perUnitToCentsString(parseFloat(ourPrice), unitsPerPack));
      return;
    }
    // If the user blurred with the focus-seeded value (rounded display)
    // untouched, treat it as a no-op — otherwise sub-cent pack prices
    // (e.g. $1.07 / 10 = $0.107) would silently round up to $1.10 just
    // because the rep tabbed through.
    if (value === perUnitToCentsString(parseFloat(ourPrice), unitsPerPack)) {
      return;
    }
    // Round to cents so non-integer ratios (e.g. 1.25 × 9.9999999) don't
    // commit values like "12.500000000000002" or flag clean blurs as dirty.
    const newPackCents = Math.round(parsed * unitsPerPack * 100);
    const currentPackCents = Math.round(parseFloat(ourPrice) * 100);
    if (newPackCents === currentPackCents) {
      return;
    }
    onUpdatePrice(localId, (newPackCents / 100).toString());
  };

  return (
    <View style={styles.container}>
      <TextInput
        keyboardType="decimal-pad"
        onBlur={handleBlur}
        onChangeText={handleChangeText}
        onFocus={handleFocus}
        placeholder="0.00"
        placeholderTextColor={theme.sk.color.text.tertiary}
        selectTextOnFocus
        style={[styles.input, isFocused && styles.inputFocused]}
        value={
          isFocused
            ? value
            : formatPerUnit(ourPrice, unitsPerPack, currencyCode)
        }
      />
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignItems: 'flex-start',
    flex: 1,
    justifyContent: 'center',
  },
  input: {
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    color: theme.sk.color.text.primary,
    fontFamily: theme.sk.typography.paragraph.md.fontFamily,
    fontSize: theme.sk.typography.paragraph.md.fontSize,
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceSmall,
    textAlign: 'left',
    width: '100%',
  },
  inputFocused: {
    borderColor: theme.sk.color.border.input.active,
  },
}));
